import React, { useState, useEffect, useRef } from "react";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import FacebookIcon from "components/icons/Facebook";
import InstagramIcon from "components/icons/Instagram";
import LinkedinIcon from "components/icons/Linkedin";
import YoutubeIcon from "components/icons/Youtube";
import PinterestIcon from "components/icons/Pinterest";
import styled from "styled-components";
import { Section, Container } from "components/shared";
import cx from "classnames";
import breakpoints from "utils/breakpoints";
import pageTypes from "utils/pageTypes";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { useLocation } from "react-router-dom";
import { scrollToSection } from "utils/scrollTo";
import { Link } from "react-router-dom";
import OpenNewTab from "components/icons/OpenNewTab";
import disclaimerLogo from "assets/disclaimerLogo.png";
import { useCountryValue } from "stores/country";
import Helmet from "react-helmet";

const COMPONENTS_HEIGHT = {
  gallery: 100,
  contact: 60,
  sitemap: 80,
  whatsnearby: 40,
  amenities: 60,
};
const StyledSection = styled(Section)`
  @media print {
    display: none;
  }
`;

const AquaBar = styled.div`
  @media ${breakpoints.md} {
    width: 7.5rem;
  }
`;

const GrayBar = styled.div`
  @media ${breakpoints.md} {
    margin-top: 4.375rem;
  }
`;
const CopyrightContainer = styled.div`
  margin-bottom: 5rem;
  @media ${breakpoints.md} {
    margin-bottom: 3.75rem;
  }
`;
const Img = styled.img`
  ${({ isHorizontal }) =>
    isHorizontal ? "max-height: 6.25rem;" : "max-height: 12.5rem;"}
`;

const getIcon = (icon) => {
  switch (icon) {
    case "facebook":
      return <FacebookIcon />;
    case "instagram":
      return <InstagramIcon />;
    case "pinterest":
      return <PinterestIcon />;
    case "linkedin":
      return <LinkedinIcon />;
    case "youtube":
      return <YoutubeIcon />;
    default:
      return null;
  }
};

const getSocialLabel = (icon) => {
  switch (icon) {
    case "facebook":
      return "Facebook (opens in a new tab)";
    case "instagram":
      return "Instagram (opens in a new tab)";
    case "pinterest":
      return "Pinterest (opens in a new tab)";
    case "linkedin":
      return "Linkedin (opens in a new tab)";
    case "youtube":
      return "Youtube (opens in a new tab)";
    default:
      return null;
  }
};

const getSocialMediaHref = (icon, href) => {
  const profile = href.split("/")[3];
  switch (icon) {
    case "facebook":
      return "fb://profile/" + profile;
    case "instagram":
      return "instagram://user?username=" + profile;
    default:
      return href;
  }
};

const Footer = ({ fields, isMetroSearch = false, showMap = false }) => {
  // Fields
  const navigation = getFieldValue(fields, "navigation") || [];
  const social = getFieldValue(fields, "social") || [];
  const copyright = getFieldValue(fields, "copyright");
  const legalLinks = getFieldValue(fields, "legalLinks") || [];
  const disclamer = getFieldValue(fields, "disclamer");
  const news = getFieldValue(fields, "news") || [];
  const organizationalschema = getFieldValue(fields, "organizationalschema");

  const currentPageType = getFieldValue(fields, "currentPageType");
  const logo = getFieldValue(fields, "logo");
  const [isHorizontal, setIsHorizontal] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const imgRef = useRef();
  useEffect(() => {
    if (imgRef.current) {
      const imgHeight = imgRef.current.naturalHeight;
      const imgWidth = imgRef.current.naturalWidth;
      setIsHorizontal(imgWidth > imgHeight);
    }
  }, [imgRef]);

  const [withAnchorCTA, setWithAnchorCTA] = useState(false);

  const columns = navigation.reduce((cols, el) => {
    cols[el.sectionName] = [...(cols[el.sectionName] || []), el];
    return cols;
  }, {});

  const firstCol = columns[Object.keys(columns)[0]] || [];
  const secondCol = columns[Object.keys(columns)[1]] || [];
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const hasAnchorCTA =
    currentPageType === pageTypes.CommunityPage ||
    currentPageType === pageTypes.CondoPage ||
    currentPageType === pageTypes.QmiPage ||
    currentPageType === pageTypes.PlanPage;
  const [{ country }] = useCountryValue();

  useEffect(() => {
    const anchorCTA = document.getElementById("anchor-cta");
    if (anchorCTA) {
      setWithAnchorCTA(true);
    }
  }, []);
  useEffect(() => {
    if (document && document.URL && organizationalschema) {
      organizationalschema.url = document.URL.split("?")[0].split("#")[0];
    }
    //console.log(document.URL)
  }, [organizationalschema]);

  const location = useLocation();
  const hash = location.hash;
  useEffect(() => {
    if (
      hash &&
      !(hash.substring(1) === "visualizer" || hash.substring(1) === "features")
    )
      setTimeout(() => {
        const heightScroll = COMPONENTS_HEIGHT[hash.substring(1)] || 80;
        scrollToSection(hash.substring(1), heightScroll);
      }, 0);
  }, [hash]);

  useEffect(() => {
    const mainElements = document.getElementsByTagName("main")[0];
    for (const element of mainElements.childNodes) {
      //const element = element.getAttribute("data-status");
      if (element) {
        setShowFooter(true);
        // Grab the data
      }
    }
  }, []);
  if (!showFooter) return "";

  return (
    <>
      <Helmet>
        <script defer type="application/ld+json">
          {JSON.stringify(organizationalschema)}
        </script>
      </Helmet>
      <StyledSection
        id="footer"
        tabIndex={"-1"}
        className={cx("bg-footer flex flex-col items-center pt-12 lg:mb-0", {
          "pb-12": !hasAnchorCTA,
          "pb-32": hasAnchorCTA,
          "mb-16": withAnchorCTA,
        })}
        wScreen={""}
        role="contentinfo"
        aria-label="footer"
      >
        <Container>
          <div
            className={cx("px-0 lg:px-12 ", {
              "md:mx-8 lg:mx-0": isMetroSearch && showMap,
            })}
          >
            <div className="flex justify-between flex-wrap">
              <div
                className={cx("", {
                  "w-5/12": !isMetroSearch || (isMetroSearch && showMap),
                  "md:w-4/12":
                    (!isMetroSearch || (isMetroSearch && !showMap)) &&
                    !news.length > 0,
                  "md:w-3/12":
                    (!isMetroSearch || (isMetroSearch && !showMap)) &&
                    news.length > 0,
                })}
              >
                <h2 className="text-white font-trade-gothic-20 text-2xl font-bold leading-9">
                  {(firstCol[0] || {}).sectionName}
                </h2>
                <AquaBar className="border-b-2 border-aqua w-10/12"></AquaBar>
                <ul className="pt-2">
                  {firstCol.map((item) => (
                    <li
                      key={item.text}
                      className="text-white font-graphie text-sm leading-8 tracking-wide"
                    >
                      <a
                        href={item.href}
                        target={item.target || "_self"}
                        aria-label={
                          item.text +
                          (item && item.target && item.target === "_blank"
                            ? ". Opens in new tab"
                            : "")
                        }
                        className="hover:underline flex"
                      >
                        {item.text}
                        {item && item.target && item.target === "_blank" && (
                          <OpenNewTab className="w-4 " />
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className={cx("", {
                  "w-7/12": !isMetroSearch || (isMetroSearch && showMap),
                  "md:w-4/12":
                    (!isMetroSearch || (isMetroSearch && !showMap)) &&
                    !news.length > 0,
                  "md:w-3/12":
                    (!isMetroSearch || (isMetroSearch && !showMap)) &&
                    news.length > 0,
                })}
              >
                <div className="flex flex-col items-start">
                  <h2 className="text-white font-trade-gothic-20 text-2xl font-bold leading-9">
                    {(secondCol[0] || {}).sectionName}
                  </h2>
                  <AquaBar className="border-b-2 border-aqua w-10/12"></AquaBar>
                  <ul className="pt-2">
                    {secondCol.map((item) => (
                      <li
                        key={item.text}
                        className="text-white font-graphie text-sm leading-8"
                      >
                        <a
                          href={item.href}
                          target={item.target || "_self"}
                          className="hover:underline whitespace-nowrap flex"
                          aria-label={
                            item.text +
                            (item && item.target && item.target === "_blank"
                              ? ". Opens in new tab"
                              : "")
                          }
                        >
                          {item.text}
                          {item && item.target && item.target === "_blank" && (
                            <OpenNewTab className="w-4 " />
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {news.length > 0 && (
                <div
                  className={cx("", {
                    "w-7/12": isMetroSearch && showMap,
                    "w-1/2 md:w-3/12 mt-16 md:mt-0":
                      !isMetroSearch || (isMetroSearch && !showMap),
                  })}
                >
                  <div className="flex flex-col items-start">
                    <h2 className="text-white font-trade-gothic-20 text-2xl font-bold leading-9">
                      {(news[0] || {}).sectionName}
                    </h2>
                    <AquaBar className="border-b-2 border-aqua w-10/12"></AquaBar>
                    <ul className="pt-2">
                      {news.map((item) => (
                        <li
                          key={item.text}
                          className="text-white font-graphie text-sm leading-8 tracking-wide"
                        >
                          <a
                            href={item.href}
                            target={item.target || "_self"}
                            aria-label={
                              item.text +
                              (item && item.target && item.target === "_blank"
                                ? ". Opens in new tab"
                                : "")
                            }
                            className="hover:underline flex"
                          >
                            {item.text}
                            {item &&
                              item.target &&
                              item.target === "_blank" && (
                                <OpenNewTab className="w-4" />
                              )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div
                className={cx("", {
                  "md:items-end mt-16 md:mt-0":
                    !isMetroSearch || (isMetroSearch && isMobile),
                  " mt-16 w-7/12": isMetroSearch && showMap && news.length > 0,
                  " mt-16 w-auto": isMetroSearch && showMap && !news.length > 0,
                  " md:w-4/12":
                    (!isMetroSearch || (isMetroSearch && !showMap)) &&
                    !news.length > 0,
                  "md:w-3/12":
                    (!isMetroSearch || (isMetroSearch && !showMap)) &&
                    news.length > 0,
                })}
              >
                <div className="flex flex-col">
                  <h2 className="text-white font-trade-gothic-20 text-2xl font-bold leading-9">
                    {(social[0] || {}).sectionName}
                  </h2>
                  <div className="border-b-2 border-aqua w-full"></div>
                  <div className="flex items-center justify-between md:justify-start pt-6">
                    {social &&
                      social.length &&
                      social.map((item, i) => (
                        <a
                          className={cx("text-white h-8 w-8", {
                            "ml-2": i !== 0,
                            "mr-2": i !== social.length - 1,
                          })}
                          href={
                            isMobile
                              ? getSocialMediaHref(item.icon, item.href)
                              : item.href
                          }
                          key={item.icon}
                          target={"_blank"}
                          aria-label={getSocialLabel(item.icon)}
                        >
                          {getIcon(item.icon)}
                        </a>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <GrayBar className="w-full border-b-2 border-mattamy-gray-light mb-8 md:mb-10 mt-20"></GrayBar>
            <CopyrightContainer className="flex flex-col md:flex-row-reverse justify-center items-center md:justify-between text-white text-sm font-graphie">
              <p className="mb-4 md:mb-0 text-center md:ml-10 flex flex-wrap justify-center">
                {Array.isArray(legalLinks) &&
                  legalLinks.map((link, index) => (
                    <>
                      <a
                        key={link.text}
                        href={link.href}
                        target={
                          link.href.includes("javascript") || link.target === ""
                            ? "_self"
                            : link.target
                        }
                        className="hover:underline flex "
                        style={{ paddingTop: "2px", paddingBottom: "2px" }}
                        aria-label={
                          link.text +
                          (link.href.includes("javascript") ||
                          link.target === ""
                            ? ""
                            : link && link.target && link.target === "_blank"
                            ? ". Opens in new tab"
                            : "")
                        }
                        {...(legalLinks.length - 1 === index && {
                          id: "LastFooterLink",
                        })}
                      >
                        {link.text}
                        {link && link.target && link.target === "_blank" && (
                          <OpenNewTab className="w-4 ml-1" />
                        )}
                        {index < legalLinks.length - 1 && (
                          <span className="mx-2">|</span>
                        )}
                      </a>
                    </>
                  ))}
              </p>
              <p className="text-center flex h-7 items-center">
                {country === "USA" && (
                  <img
                    className="object-contain mr-1"
                    src={disclaimerLogo}
                    alt="copyright disclaimer logo"
                  />
                )}{" "}
                {copyright} {disclamer}
              </p>
            </CopyrightContainer>

            <div className="flex flex-col items-center text-center">
              {isMetroSearch && logo && (
                <Link to="/">
                  <Img
                    className="mb-12"
                    ref={imgRef}
                    isHorizontal={isHorizontal}
                    src={logo}
                    aria-label={"Mattamy Logo. Go to Homepage"}
                    alt={"Mattamy Logo"}
                  />
                </Link>
              )}
            </div>
          </div>
        </Container>
      </StyledSection>
    </>
  );
};
export default Footer;
